import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Courrier, CourrierFilters } from '../../types/api';

interface CourriersState {
  courriers: Courrier[];
  currentCourrier: Courrier | null;
  filters: CourrierFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: CourriersState = {
  courriers: [],
  currentCourrier: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  isLoading: false,
  error: null,
};

export const courriersSlice = createSlice({
  name: 'courriers',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setCourriers: (state, action: PayloadAction<{
      courriers: Courrier[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>) => {
      state.courriers = action.payload.courriers;
      state.pagination = {
        total: action.payload.total,
        page: action.payload.page,
        limit: action.payload.limit,
        totalPages: action.payload.totalPages,
      };
      state.isLoading = false;
      state.error = null;
    },
    addCourrier: (state, action: PayloadAction<Courrier>) => {
      state.courriers.unshift(action.payload);
      state.pagination.total += 1;
    },
    updateCourrier: (state, action: PayloadAction<Courrier>) => {
      const index = state.courriers.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.courriers[index] = action.payload;
      }
      if (state.currentCourrier?.id === action.payload.id) {
        state.currentCourrier = action.payload;
      }
    },
    removeCourrier: (state, action: PayloadAction<number>) => {
      state.courriers = state.courriers.filter(c => c.id !== action.payload);
      state.pagination.total -= 1;
    },
    setCurrentCourrier: (state, action: PayloadAction<Courrier | null>) => {
      state.currentCourrier = action.payload;
    },
    setFilters: (state, action: PayloadAction<CourrierFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 1; // Reset to first page when filters change
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    clearCourriers: (state) => {
      state.courriers = [];
      state.currentCourrier = null;
      state.pagination = initialState.pagination;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setError,
  setCourriers,
  addCourrier,
  updateCourrier,
  removeCourrier,
  setCurrentCourrier,
  setFilters,
  setPage,
  clearCourriers,
} = courriersSlice.actions;